<script setup lang="ts">
import formValidationFunctions, { type FormValidationFunctionParams, type ValidationName, type ValidationResult } from '@/ts/form-validation-functions.ts';
import { onElemVisible, pad } from '@/ts/utils/utils.ts';
import { getScript } from '@/ts/utils/model-utils';
import { getLangText } from '@/ts/utils/model-utils';
import { nextTick, provide, reactive, type CSSProperties, type HTMLAttributes, type InputHTMLAttributes, type ObjectDirective } from 'vue';
import { computed, onMounted, ref, watch, type StyleValue } from 'vue';
import FormInputSelect from './form-input-select.vue';
import type { SelectElementListItem } from '@/ts/types/dom-types';
import type { FormInputType } from '@/ts/types/form-types';
import model from '@/ts/model';
import type { PreloadImgExpErrVals } from '@/ts/directives/v-preload-img';
import vPreloadImg from '@/ts/directives/v-preload-img';
import DangerIcon from '@/assets/icons/danger-icon.svg?raw';
import LockIcon from '@/assets/icons/lock-icon.svg?raw';
import EyeIcon from '@/assets/icons/eye-icon.svg?raw';
import EyeCrossedIcon from '@/assets/icons/eye-crossed-icon.svg?raw';
import type { LangTextPlaceholders } from '@/ts/types/lang-label-types.ts';
// import type { ServerMessageCodes } from '@/ts/enum/server-message-codes.ts';
// import type { DisposeListenerTrigger } from '@/ts/types/main-model.ts';

export type FormInput = {
    type: FormInputType,
    autocomplete?: string,
    placeholder?: string,
    placeholderText?: string,
    text?: string,
    validation?: ValidationName,
    validationParams?: FormValidationFunctionParams,
    list?: {
        data?: any[], 
        url?: string,
        keyName?: string, 
        valName?: string,
        valLabel?: string,
        searchBoxRequest?: (value: string) => Promise<SelectElementListItem[]>,
        searchBoxImageRight?: (item: SelectElementListItem) => string | undefined,
        getImageLeft?: (item?: SelectElementListItem) => Record<string, unknown> | PreloadImgExpErrVals | string | undefined,
        searchBoxEmptyInputLabel?: string,
        searchField?: boolean,
    },
    style?: StyleValue,
    inputValidationGroup?: string,
    disabled?: boolean,
    addon?: string,
    addonPosition?: 'left' | 'right',
    showDefaultOption?: boolean,
    renderDefaultOption?: boolean,
    selectDropdownStyle?: CSSProperties,
    extraClass?: string,
    name?: string,
    value?: string | boolean,
    focusChangeDelay?: number,
    displayText?: string,
    onInput?: (payload: InputEvent) => void,
    onChange?: (payload: Event) => void,
    inputmode?: HTMLAttributes['inputmode'],
    pattern?: InputHTMLAttributes['pattern'],
    // responseErrorCode?: ServerMessageCodes,
}

provide('from-form-input-comp', true);

const props = withDefaults(defineProps<FormInput>(), {
    showDefaultOption: false,
    renderDefaultOption: true,
    addonPosition: 'right',
});

const focus = defineModel<boolean>('focus');
const input = defineModel<string | number | boolean | undefined>('input');
const extraInput = defineModel<string | number | boolean | undefined>('extra-input');
const dateInputs = reactive(<{day: string, month: string, year: string}>{
    day: '',
    month: '',
    year: '',
});

const elemFormInput = ref<HTMLElement>();

const elemDateDay = ref<HTMLElement>();
const elemDateMonth = ref<HTMLElement>();
const elemDateYear = ref<HTMLElement>();

const inputId = `input-${props.type}-${crypto.randomUUID()}`;
const ignoreError = defineModel<boolean>('ignore-error');
ignoreError.value = true;

const passwordMasked = ref<boolean>(true);

const computedType = computed<FormInputType>(() => {
    if(props.type == 'clabe') return 'text';
    if(props.type == 'password') return passwordMasked.value ? props.type : 'text';
    return props.type;
});

const validationResult = ref<ValidationResult>('none');
const validationMessage = ref<string>('');
const validationMessageParams = ref<LangTextPlaceholders>({});
// const serverErrorHappened = ref<boolean>(false);

let inputScript = ref<string>();
let inputScriptLoading = ref<boolean>(false);

watch(() => validationResult.value, value => {
    if(value != 'error' && value != 'none') ignoreError.value = false;
});

let validation: ValidationName | undefined = props.validation;
if(props.type == 'cvv') validation = 'validate-cvv';
if(props.type == 'expDate') validation = 'validate-exp-date';
if(props.type == 'clabe') {
    let clabeScript = model.clientConfig.scripts?.clabe;
    if(clabeScript){
        validation = 'validate-clabe';
        inputScript.value = clabeScript;
        inputScriptLoading.value = true;
    }
}

if(validation){
    let validationFunction = formValidationFunctions(validation, validationResult, validationMessage, validationMessageParams/*, serverErrorHappened, props.responseErrorCode*/);

    onMounted(() => {
        if(!validationFunction) return;

        if(elemFormInput.value){
            elemFormInput.value.checkValidation = (forced?: boolean) => {
                if(forced){
                    validationFunction(input.value, getParams());
                    return validationResult.value == 'success';
                }

                ignoreError.value = false;
                if(validationResult.value == 'none'){
                    validationFunction(input.value, getParams());
                    return false;
                }
                return validationResult.value == 'success';
            };
        }

        watch(() => input.value, value => {
            // serverErrorHappened.value = false;
            validationFunction(value, getParams());
        });

        let group = props.inputValidationGroup;
        if(group){
            let form = elemFormInput.value?.closest('form');
            if(form){
                let elemInputs = form.querySelectorAll(`.form-input[data-validation-group="${group}"]`);
                if(elemInputs.length == 2){
                    let otherElemInput = Array.from(elemInputs).find(x => x != elemFormInput.value);
                    watch(() => input.value, async () => {
                        await nextTick();
                        otherElemInput?.checkValidation?.(true);
                    });
                }
            }
        }

        function getParams(): FormValidationFunctionParams{
            return { extraInput: extraInput.value, ...props.validationParams };
        }
    });
}

if(props.type == 'date' || props.type == 'expDate'){
    watch(() => dateInputs.day, value => {
        if(value) value = stripAllNonNumericChars(value);
        if(value.length >= 2) elemDateMonth.value?.focus();
        dateInputs.day = value;
    });

    watch(() => dateInputs.month, value => {
        if(value) value = stripAllNonNumericChars(value);
        if(value.length >= 2) elemDateYear.value?.focus();
        dateInputs.month = value;
    });

    watch(() => dateInputs.year, value => {
        if(value) value = stripAllNonNumericChars(value);
        dateInputs.year = value;
    });

    [
        () => dateInputs.day,
        () => dateInputs.month,
        () => dateInputs.year,
    ].forEach(cbx => {
        watch(cbx, () => {
            switch(props.type){
                case 'date' : 
                input.value = `${dateInputs.year}-${dateInputs.month}-${dateInputs.day}`;
                break;
                case 'expDate' : 
                input.value = `${dateInputs.month}-${dateInputs.year}`;
                break;
            }
        });
    });
}

function expDateInputBlur(){
    if(elemDateMonth.value){
        dateInputs.month = pad(Number(dateInputs.month));
    }
}

watch(() => focus.value, async value => {
    if(props.type != 'text' && props.type != 'number') return;

    let elemInput = elemFormInput.value?.querySelector('input');
    if(!elemInput) return;

    if(value) elemInput.focus();
});

function onDateWrapperClick(){
    if(dateInputs.month && elemDateYear.value){
        elemDateYear.value.focus();
        return;
    }

    if(dateInputs.day && elemDateMonth.value){
        elemDateMonth.value.focus();
        return;
    }

    if(elemDateDay.value) elemDateDay.value.focus();
    else if(elemDateMonth.value) elemDateMonth.value.focus();
}

async function onFocus(){
    if(props.focusChangeDelay){
        setTimeout(() => focus.value = true, props.focusChangeDelay);
        return;
    }

    focus.value = true;
}

function onBlur(){
    if((props.type == 'text' || props.type == 'email' || props.type == 'clabe') && typeof input.value === 'string' && input.value) input.value = input.value.trim();

    if(props.focusChangeDelay){
        setTimeout(() => focus.value = false, props.focusChangeDelay);
        return;
    }

    focus.value = false;
}

const vLoadInputScript = {
    mounted(element: HTMLElement){
        const scriptSrc = inputScript.value;
        if(!scriptSrc) return;

        const observer = onElemVisible(element, async () => {
            await getScript(scriptSrc);
            inputScriptLoading.value = false;
            observer.disconnect();
        });
    },
};

function onInputCVV(){
    let value = input.value;
    if(!value) value = '';
    value = value + '';
    input.value = stripAllNonNumericChars(value);
}

function stripAllNonNumericChars(value: string){
    return value.replaceAll('.', '').replaceAll('-', '').replace(/\D/g, "").trim();
}

// let inputResponseErrorDisposeTrigger: DisposeListenerTrigger | undefined;

// const vInputResponseError: ObjectDirective<HTMLInputElement> = {
//     unmounted(){
//         inputResponseErrorDisposeTrigger?.();
//         inputResponseErrorDisposeTrigger = undefined;
//     },
//     mounted(element){
//         if(!validation) return;

//         const value = props.responseErrorCode;
//         if(!value) return;

//         inputResponseErrorDisposeTrigger = model.on('RegisterFailureByClientMessageCode', async e => {
//             const code = (e as CustomEvent).detail?.code as ServerMessageCodes | undefined;
//             if(code != value) return;

//             serverErrorHappened.value = true;

//             for(let i = 0; i < 10; i++){
//                 model.dispatch('RegisterFailureByClientMessageCodeResolve');
//                 await nextTick();

//                 if(element.offsetParent) break;
//             }

//             focus.value = true;
//             await nextTick();

//             elemFormInput.value?.checkValidation?.(true);
//         });
//     },
// };

async function onInputInner(payload: InputEvent){
    if(typeof props.onInput !== 'function') return;
    await nextTick(); //firefox hack
    return props.onInput(payload);
}

</script>

<template>
    <div 
        :class="[
            extraClass,
            {
                'checkbox': type == 'checkbox',
                'radio': type == 'radio',
                'validation-success': validationResult == 'success',
                'validation-error': validationResult == 'error' && !ignoreError,
                'validation-waiting-for-server': validationResult == 'waiting-for-server',
                'disabled': disabled,
                'focused': focus,
            },
        ]" 
    class="form-input" ref="elemFormInput" :data-type="type" :data-validation="validation" :data-validation-group="inputValidationGroup" v-mobile>
    <!--class="form-input" ref="elemFormInput" :data-type="type" :data-validation="validation" :data-validation-group="inputValidationGroup" v-mobile v-input-response-error>-->
        <template v-if="list?.getImageLeft?.()">
            <div class="img-left">
                <img v-preload-img="list?.getImageLeft?.()">
            </div>
        </template>
        <template v-if="type == 'empty'">
            <div class="empty-input-placeholder">
                <slot></slot>
            </div>
        </template>
        <template v-else-if="type == 'cvv'">
            <input 
                @input="onInputCVV" 
                type="text" 
                autocomplete="cc-csc" 
                inputmode="numeric"
                pattern="\d{3,4}"
                maxlength="4"
                placeholder="123"
                @focus="onFocus()" 
                @blur="onBlur(); ignoreError = false;" 
                v-model="input" 
                required 
                :style="style" 
                :disabled="disabled" 
            />
        </template>
        <template v-else-if="type == 'date'">
            <div class="date-wrapper" @click.prevent.stop="onDateWrapperClick()">
                <input class="day" type="text" ref="elemDateDay" maxlength="2" :placeholder="getLangText('BirthdayformatDay')" v-model="dateInputs.day" required @click.prevent.stop :disabled="disabled" inputmode="numeric" /><span>/</span>
                <input class="month" type="text" ref="elemDateMonth" maxlength="2" :placeholder="getLangText('BirthdayformatMonth')" v-model="dateInputs.month" required @click.prevent.stop :disabled="disabled" inputmode="numeric" /><span>/</span>
                <input class="year" type="text" ref="elemDateYear" maxlength="4" :placeholder="getLangText('BirthdayformatYear')" v-model="dateInputs.year" required @click.prevent.stop :disabled="disabled" inputmode="numeric" />
            </div>
        </template>
        <template v-else-if="type == 'expDate'">
            <div class="date-wrapper" @click.prevent.stop="onDateWrapperClick()">
                <input class="month" type="text" ref="elemDateMonth" maxlength="2" :placeholder="getLangText('BirthdayformatMonth')" v-model="dateInputs.month" required @click.prevent.stop :disabled="disabled" inputmode="numeric" @blur="expDateInputBlur()" /><span>/</span>
                <input class="year" type="text" ref="elemDateYear" maxlength="4" :placeholder="getLangText('BirthdayformatYear')" v-model="dateInputs.year" required @click.prevent.stop :disabled="disabled" inputmode="numeric" />
            </div>
        </template>
        <template v-else-if="type == 'select'">
            <form-input-select v-model:input="input" v-bind="{validationMessageParams, ...props}" />
        </template>
        <template v-else-if="(type == 'checkbox' || type == 'radio') && typeof input === 'boolean'">
            <input 
                :type="type" 
                :id="inputId" 
                v-model="input" 
                required 
                :disabled="disabled" 
                :name="name" 
                :value="value" 
                @change="onChange" 
            />
            <label :for="inputId">
                <div :class="type == 'checkbox' ? 'cbx' : 'radio'"></div>
                <span v-html="text" v-mobile></span>
            </label>
        </template>
        <template v-else>
            <template v-if="addon && addonPosition == 'left'">
                <div class="addon">{{ addon }}</div>
            </template>
            <template v-if="displayText">
                <div class="display-text"
                    :style="style" 
                    :class="{'has-addon': addon}" 
                >{{ displayText }}</div>
            </template>
            <template v-else>
                <template v-if="type == 'password'">
                    <div class="icon-password-lock" v-html="LockIcon"></div>
                </template>
                <input 
                    @input="onInputInner" 
                    :type="computedType" 
                    :autocomplete="autocomplete" 
                    :placeholder="placeholderText || getLangText(placeholder, validationMessageParams)" 
                    @focus="onFocus()" 
                    @blur="onBlur(); ignoreError = false;" 
                    v-model="input" 
                    required 
                    :style="style" 
                    :disabled="disabled || inputScriptLoading" 
                    :class="{'has-addon': addon}" 
                    :pattern="pattern"
                    :inputmode="inputmode"
                />
                <template v-if="type == 'password'">
                    <div class="mask" @click.prevent.stop="passwordMasked = !passwordMasked" v-html="passwordMasked ? EyeIcon : EyeCrossedIcon"></div>
                </template>
            </template>
            <template v-if="addon && addonPosition == 'right'">
                <div class="addon">{{ addon }}</div>
            </template>
        </template>
        <template v-if="validationResult == 'waiting-for-server'">
            <div class="loader"></div>
        </template>
        <template v-if="inputScriptLoading">
            <div class="loading-input-script" v-load-input-script></div>
        </template>
        <div class="validation-error-icon" v-html="DangerIcon"></div>
    </div>
    <template v-if="validationResult == 'error' && validationMessage && !ignoreError">
        <div class="error-message">{{ getLangText(validationMessage, validationMessageParams) }}</div>
    </template>
</template>

<style lang="scss" scoped>
@use '@/scss/shared';
@use 'sass:math';
$loader-dim: 22px;
$loading-color: rgb(255, 145, 0);
$error-color: #ff3232;
.form-input{
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    & > .icon-password-lock{
        display: flex;
        justify-content: center;
        align-items: center;
        flex: 0;
        padding-right: 10px;
        @include shared.site() using($site-key){
            color: shared.theme-color('comp3_3', $site-key);
        }
    }
    & > .validation-error-icon{
        position: absolute;
        display: flex;
        justify-content: center;
        align-items: center;
        right: 12px;
        top: 12px;
        z-index: 1;
        display: none;
        pointer-events: none;
        color: $error-color;
    }
    &.validation-error{
        & > .validation-error-icon{
            display: block;
        }
        & > .mask{
            right: 45px !important;
        }
    }
    & > *{
        flex: 1;
    }
    & > .img-left{
        display:flex;
        justify-content: center;
        align-items: center;
        flex: 0 0 30px;
        align-self: stretch;
        margin-right: 5px;
        & > img{
            display: block;
            width: 100%;
            animation-name: img-display;
            animation-duration: .25s;
            animation-fill-mode: forwards;
            @keyframes img-display {
                from{
                    opacity: 0;
                }
                to{
                    opacity: 1;
                }
            }
        }
    }
    &.disabled{
        & > input,
        & :deep(select){
            cursor: not-allowed !important;
        }
    }
    &[data-type="hidden"]{
        padding: 0 !important;
        border: none !important;
    }
    & > .addon{
        flex: 0 0 10px;
        font-weight: 500 !important;
        user-select: none;
        font-size: 14px;
        color: #fff;
    }
    & > .date-wrapper{
        display: flex;
        justify-content: flex-start;
        align-items: center;
        font-weight: 500;
        cursor: text;
        & > input{
            text-align: center;
            outline: none;
            border: none;
            height: 37px;
            font-weight: 500 !important;
            font-size: 14px;
            background-color: transparent;
            color: #fff;
            &::placeholder{
                color: #fff;
            }
            &.day,
            &.month {
                flex: 0 0 20px;
                width: 24px;
            }
            &.year {
                flex: 0 0 40px;
                width: 40px;
            }
        }
    }
    & > .loader,
    & > .loading-input-script{
        position: absolute;
        width: $loader-dim;
        height: $loader-dim;
        top: 12px;
        border-left-color: transparent !important;
        border-radius: 100%;
        animation-fill-mode: forwards;
        animation-duration: 1s;
        animation-name: form-input-loader;
        animation-timing-function: linear;
        animation-iteration-count: infinite;
        @keyframes form-input-loader {
            from{
                transform: rotate(0deg);
            }
            to{
                transform: rotate(359deg);
            }
        }
    }
    & > .loader{
        right: 12px;
        border: 2px solid $loading-color;
    }
    & > .loading-input-script{
        right: calc(50% - math.div($loader-dim, 2));
        @include shared.site() using($site-key){
            border: 2px solid shared.theme-color("comp3_3", $site-key);
        }
    }
    &.checkbox,
    &.radio{
        position: relative;
        & :deep(label){
            @include shared.dashed-link('neutral_1');
            color: #fff !important;
            font-weight: 500 !important;
        }
        & > input,
        & > label{
            cursor: pointer;
        }
        & > input{
            position: absolute;
            width: 0;
            height: 0;
            left: 0;
            top: 0;
            &:active ~ label{
                & > .cbx,
                & > .radio{
                    @include shared.site() using($site-key){
                        box-shadow: 0 0 0 2px shared.theme-color("comp3_3", $site-key);
                    }
                }
            }
            &:checked ~ label{
                & > .cbx,
                & > .radio{
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
                & > .cbx:after{
                    content: "";
                    display: block;
                    width: 4px;
                    height: 8px;
                    border: 2px solid #fff;
                    border-left:none;
                    border-top:none;
                    transform: rotate(45deg);
                    margin-bottom: 3px;
                    margin-left: 1px;
                }
                & > .radio:after{
                    content: "";
                    display: block;
                    width: 7px;
                    height: 7px;
                    background-color: #fff;
                    border-radius: 100%;
                }
            }
        }
        & label{
            display: flex;
            justify-content: center;
            align-items: center;
            & > .cbx,
            & > .radio{
                flex: 0 0 15px;
                width: 15px;
                height: 15px;
                border-radius: 2px;
                margin-right: 5px;
                @include shared.site() using($site-key){
                    background-color: shared.theme-color("primary_5", $site-key);
                    border: 2px solid shared.theme-color("comp3_3", $site-key)
                }
            }
            & > .radio{
                border-radius: 100% !important;
            }
            & > span[data-mobile]{
                font-size: 12px;
                color: #fff;
            }
        }
    }
    &:not(.checkbox):not(.radio){
        position: relative;
        width: 100%;
        background-color: #00000080;
        padding: 5px 15px;
        box-sizing: border-box;
        border-radius: 10px;
        transition: border-color .25s;
        color: #fff;
        @include shared.site() using($site-key){
            border-bottom: 1px solid shared.theme-color("comp3_3", $site-key);
        }
        & > .empty-input-placeholder,
        & > input,
        & > .display-text,
        & :deep(select){
            width: 100%;
            box-sizing: border-box;
            outline: none;
            border: none;
            background-color: transparent;
            transition: background-color 10000000s, color 10000000s;
            height: 40px;
            font-weight: 700;
            font-size: 14px;
            color: #fff !important;
            &::placeholder{
                color: #fff;
            }
        }
        & > .display-text {
            display: flex;
            justify-content: flex-start;
            align-items: center;
        }
        & :deep(select){
            cursor: pointer;
        }
        & > .mask{
            display: flex;
            position: absolute;
            top:13px;
            right:22px;
            cursor: pointer;
            justify-content: center;
            align-items: center;
        }
        & > .empty-input-placeholder{
            display: flex;
            justify-content: center;
            align-items: center;
            & > * {
                flex: 1;
            }
        }
    }
    &.validation-success{
        border-color: #0f0 !important;
    }
    &.validation-error{
        border-color: $error-color !important;
        &.checkbox{
            & .cbx{
                border-color: $error-color !important;
            }
            & input:active ~ label > .cbx{
                box-shadow: 0 0 0 2px $error-color !important;
            }
        }
    }
    &.validation-waiting-for-server{
        border-color: $loading-color !important;
    }
    & ~ .error-message{
        color: $error-color;
        font-size: 13px;
        text-align: left;
        font-weight: 400;
        text-shadow: 1px 1px 2px #000;
    }
    &.checkbox.validation-error ~ .error-message{
        text-align: center;
    }
}
</style>